/**
 * TME Wiki drafts - autosave to localStorage ONLY (D3).
 *
 * Planning/TME-WIKI-2026-09-02.md §10.4, G8, R22.
 *
 * Only an explicit Save writes a version, so an autosave that reached the server
 * would either write a version nobody asked for or store a body with no version
 * - both of which make the history dishonest. The draft therefore never leaves
 * the browser.
 *
 * ⚠️ THE KEY CARRIES THE ADMIN'S COGNITO `sub`, AND REVISION 2's DID NOT (G8).
 * On a shared machine a key of `<wikiId>:<pageId>` alone means the NEXT admin to
 * open that page is offered the previous one's unsaved internal text, in a
 * restore banner, as though it were their own.
 *
 * ⚠️ THIS IS INTERNAL PROCEDURE TEXT IN UNENCRYPTED BROWSER STORAGE. So it is
 * cleared on Save, on Discard and on LOGOUT; at most five are kept, oldest
 * evicted; and a draft over the body limit is not stored at all - the 5MB origin
 * quota would otherwise be filled by a dozen large pages and every other
 * persisted key in the portal would start failing to write.
 */

const PREFIX = 'tmeWikiDraft:';

/** The same ceiling the server enforces on a stored body (§4.2). */
export const MAX_DRAFT_BYTES = 350000;

/** How many drafts one browser keeps. Oldest evicted. */
export const MAX_DRAFTS = 5;

/**
 * The signed-in admin's Cognito `sub`, from the id token the portal already
 * holds. Returns null when there is no usable token - the caller then stores no
 * draft at all rather than falling back to a shared key.
 */
export function adminSub() {
    try {
        const token = localStorage.getItem('adminToken');
        if (!token) return null;
        const payload = token.split('.')[1];
        if (!payload) return null;
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        const sub = JSON.parse(json).sub;
        return typeof sub === 'string' && sub ? sub : null;
    } catch (err) {
        console.error('Could not read the admin sub for a wiki draft:', err);
        return null;
    }
}

export function draftKey(sub, wikiId, pageId) {
    return `${PREFIX}${sub}:${wikiId}:${pageId}`;
}

/** Every draft key this browser holds, for any admin. */
function allDraftKeys() {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(PREFIX)) keys.push(key);
    }
    return keys;
}

function parse(key) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const draft = JSON.parse(raw);
        if (typeof draft?.contentHtml !== 'string' || typeof draft?.savedAt !== 'string') return null;
        return draft;
    } catch {
        return null;
    }
}

/**
 * Write a draft, evicting the oldest if this browser is already at the cap.
 *
 * Returns true when something was stored. An oversize body, or no `sub`, stores
 * NOTHING and says so - never a truncated draft, which would restore as a
 * silently damaged page.
 */
export function saveDraft({ sub, wikiId, pageId, title, contentHtml }) {
    if (!sub || !wikiId || !pageId) return false;

    const bytes = new TextEncoder().encode(contentHtml || '').length;
    if (bytes > MAX_DRAFT_BYTES) {
        console.warn(`TME wiki draft not stored: ${bytes} bytes exceeds ${MAX_DRAFT_BYTES}`);
        return false;
    }

    const key = draftKey(sub, wikiId, pageId);
    const existing = allDraftKeys();
    if (!existing.includes(key) && existing.length >= MAX_DRAFTS) {
        const oldest = existing
            .map(k => ({ key: k, savedAt: parse(k)?.savedAt || '' }))
            .sort((a, b) => a.savedAt.localeCompare(b.savedAt))[0];
        if (oldest) localStorage.removeItem(oldest.key);
    }

    try {
        localStorage.setItem(key, JSON.stringify({
            title: title || '',
            contentHtml: contentHtml || '',
            savedAt: new Date().toISOString()
        }));
        return true;
    } catch (err) {
        // A full quota must not take the editor down with it.
        console.error('Could not store a TME wiki draft:', err);
        return false;
    }
}

/** The draft for one page, or null. Never another admin's. */
export function readDraft({ sub, wikiId, pageId }) {
    if (!sub || !wikiId || !pageId) return null;
    return parse(draftKey(sub, wikiId, pageId));
}

export function clearDraft({ sub, wikiId, pageId }) {
    if (!sub || !wikiId || !pageId) return;
    localStorage.removeItem(draftKey(sub, wikiId, pageId));
}

/**
 * Every draft in this browser, for every admin.
 *
 * Called from the admin logout, which already clears the token: leaving
 * internal procedure text behind on a shared machine is the whole reason the
 * key carries a `sub` in the first place.
 */
export function clearAllDrafts() {
    for (const key of allDraftKeys()) localStorage.removeItem(key);
}

/**
 * Is this draft newer than what the server last stored?
 *
 * Compared against the page's `updatedAt`, so a draft written BEFORE somebody
 * else's save is not offered as a restore - it is already stale, and offering it
 * would invite an admin to overwrite a newer page with older text.
 */
export function draftIsNewerThan(draft, updatedAt) {
    if (!draft?.savedAt) return false;
    if (!updatedAt) return true;
    return draft.savedAt > updatedAt;
}
